import React, { useState, useEffect, useRef } from 'react';
import { Typography, CircularProgress, Box, useMediaQuery, useTheme, Button, IconButton } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  MenuBook as MenuBookIcon,
  ChevronRight as ChevronRightIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  CalendarMonth as CalendarMonthIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import ThemeBackground from '../components/Layout/ThemeBackground';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface PolicyDocument {
  _id: string;
  title: string;
  content: string;
  order: number;
  updatedAt: string;
}

const markdownStyles = {
  lineHeight: 1.8,
  fontSize: '0.95rem',
  color: 'rgba(255, 255, 255, 0.85)',
  '& h1': { fontSize: '1.5rem', fontWeight: 700, mt: 3, mb: 1.5, color: 'rgba(255,255,255,0.95)' },
  '& h2': { fontSize: '1.25rem', fontWeight: 600, mt: 2.5, mb: 1, color: 'rgba(255,255,255,0.95)' },
  '& h3': { fontSize: '1.1rem', fontWeight: 600, mt: 2, mb: 1, color: 'rgba(255,255,255,0.9)' },
  '& p': { mb: 1.5 },
  '& ul, & ol': { pl: 3, mb: 1.5 },
  '& li': { mb: 0.5 },
  '& a': { color: '#60a5fa', textDecoration: 'underline' },
  '& blockquote': { borderLeft: '3px solid rgba(255,255,255,0.3)', pl: 2, ml: 0, fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' },
  '& code': { backgroundColor: 'rgba(255,255,255,0.1)', px: 0.5, borderRadius: '4px', fontSize: '0.85em' },
  '& table': { width: '100%', borderCollapse: 'collapse', mb: 2, tableLayout: 'auto' },
  '& th': { border: '1px solid rgba(255,255,255,0.2)', padding: '8px 12px', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' },
  '& td': { border: '1px solid rgba(255,255,255,0.2)', padding: '8px 12px' },
  '& td:first-of-type': { whiteSpace: 'normal' },
  '& th:not(:first-of-type), & td:not(:first-of-type)': { textAlign: 'center', whiteSpace: 'nowrap' },
  '& hr': { borderColor: 'rgba(255,255,255,0.2)', my: 2 },
  '& strong': { fontWeight: 600 }
};

const PoliciesAndProcedures: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [documents, setDocuments] = useState<PolicyDocument[]>([]);
  const [guides, setGuides] = useState<PolicyDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<PolicyDocument | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        setLoading(true);
        const [policiesRes, guidesRes] = await Promise.all([
          api.get('/api/documents?category=policies'),
          api.get('/api/documents?category=guides')
        ]);
        const policyDocs = policiesRes.data || [];
        const guideDocs = guidesRes.data || [];
        setDocuments(policyDocs);
        setGuides(guideDocs);
        const all = [...policyDocs, ...guideDocs];
        if (all.length > 0) {
          setSelectedDocument(all[0]);
        }
        setError(null);
      } catch (err: any) {
        console.error('Error fetching policies:', err);
        setError('Unable to load policies. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPolicies();
  }, []);

  const handleSelectDocument = (doc: PolicyDocument) => {
    setSelectedDocument(doc);
    if (isMobile) {
      setSidebarOpen(false);
    }
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const renderDocItem = (doc: PolicyDocument) => {
    const isSelected = selectedDocument?._id === doc._id;
    return (
      <button
        key={doc._id}
        onClick={() => handleSelectDocument(doc)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
          padding: '10px 12px 10px 16px',
          textAlign: 'left',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          backgroundColor: isSelected ? 'rgba(251, 191, 36, 0.15)' : 'transparent',
          borderLeft: isSelected ? '3px solid rgb(251, 191, 36)' : '3px solid transparent',
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        <ChevronRightIcon
          sx={{
            fontSize: 16,
            color: isSelected ? 'rgb(251, 191, 36)' : 'rgba(255,255,255,0.3)',
            flexShrink: 0
          }}
        />
        <span style={{
          fontSize: '0.85rem',
          fontWeight: isSelected ? 600 : 400,
          color: isSelected ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.7)',
          lineHeight: 1.3
        }}>
          {doc.title}
        </span>
      </button>
    );
  };

  if (loading) {
    return (
      <ThemeBackground variant="full">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      </ThemeBackground>
    );
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="glass-card p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <IconButton
                    onClick={() => navigate(user?.role === 'counsellor' ? '/counsellor/dashboard' : '/')}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: '#667eea'
                      }
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography 
                    variant="h4" 
                    component="h1" 
                    className="theme-text-primary"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                    }}
                  >
                    Policies & Procedures
                  </Typography>
                </Box>
                <Typography 
                  variant="subtitle1"
                  className="theme-text-secondary"
                  sx={{
                    fontWeight: 400,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    ml: 7
                  }}
                >
                  {documents.length + guides.length} document{(documents.length + guides.length) !== 1 ? 's' : ''} available
                </Typography>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                {isMobile && (
                  <Button
                    variant="outlined"
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    startIcon={sidebarOpen ? <CloseIcon /> : <MenuIcon />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    {sidebarOpen ? 'Close Menu' : 'Open Menu'}
                  </Button>
                )}
                {user?.role === 'counsellor' && (
                <Button
                  variant="contained"
                  onClick={() => navigate('/counsellor/appointments')}
                  startIcon={<CalendarMonthIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    minWidth: { sm: '200px' },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                    boxShadow: '0 4px 15px rgba(33, 150, 243, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      boxShadow: '0 6px 20px rgba(33, 150, 243, 0.6)'
                    }
                  }}
                >
                  Appointments
                </Button>
                )}
                <Button
                  variant="contained"
                  onClick={() => navigate('/settings')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    minWidth: { sm: '200px' },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      boxShadow: '0 6px 20px rgba(16, 185, 129, 0.6)'
                    }
                  }}
                >
                  Security
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    minWidth: { sm: '120px' },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>

      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)', overflow: 'hidden' }} className="glass-card">

        {error && (
          <div style={{ padding: '12px 24px', flexShrink: 0 }}>
            <div className="glass-card p-4 border-l-4 border-red-500">
              <Typography className="theme-text-primary" sx={{ fontWeight: 500 }}>
                {error}
              </Typography>
            </div>
          </div>
        )}

        {!error && documents.length === 0 && guides.length === 0 && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="glass-card p-8 text-center">
              <MenuBookIcon sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
              <Typography className="theme-text-secondary" sx={{ fontSize: '1rem' }}>
                No policies, procedures or guides have been published yet.
              </Typography>
            </div>
          </div>
        )}

        {/* Main content area: sidebar + content */}
        {!error && (documents.length > 0 || guides.length > 0) && (
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
            
            {/* Sidebar - Tree Navigation */}
            {(sidebarOpen || !isMobile) && (
              <>
                {/* Mobile overlay backdrop */}
                {isMobile && sidebarOpen && (
                  <div 
                    onClick={() => setSidebarOpen(false)}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      zIndex: 10
                    }}
                  />
                )}
                <div 
                  style={{
                    width: isMobile ? '280px' : '300px',
                    flexShrink: 0,
                    borderRight: '1px solid rgba(255,255,255,0.1)',
                    overflowY: 'auto',
                    padding: '12px 0',
                    ...(isMobile ? {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      bottom: 0,
                      zIndex: 20,
                      backgroundColor: 'rgba(15, 23, 42, 0.98)',
                      backdropFilter: 'blur(20px)'
                    } : {})
                  }}
                >
                  {[
                    { label: 'Policies & Procedures', items: documents },
                    { label: 'Help & Guides', items: guides }
                  ].filter(section => section.items.length > 0).map(section => (
                    <div key={section.label}>
                      <div style={{ padding: '0 12px 8px 16px' }}>
                        <Typography
                          className="theme-text-secondary"
                          sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                        >
                          {section.label}
                        </Typography>
                      </div>
                      {section.items.map(doc => renderDocItem(doc))}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Content Panel */}
            <div 
              ref={contentRef}
              style={{ 
                flex: 1, 
                overflowY: 'auto', 
                padding: isMobile ? '20px 16px' : '24px 40px'
              }}
            >
              {selectedDocument ? (
                <div style={{ maxWidth: '100%' }}>
                  <Typography 
                    className="theme-text-primary" 
                    sx={{ fontWeight: 700, fontSize: isMobile ? '1.25rem' : '1.5rem', mb: 0.5 }}
                  >
                    {selectedDocument.title}
                  </Typography>
                  <Typography 
                    className="theme-text-secondary" 
                    sx={{ fontSize: '0.75rem', mb: 3, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    Last updated: {new Date(selectedDocument.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </Typography>
                  <Box sx={markdownStyles}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{selectedDocument.content}</ReactMarkdown>
                  </Box>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <div style={{ textAlign: 'center' }}>
                    <MenuBookIcon sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.2)', mb: 2 }} />
                    <Typography className="theme-text-secondary" sx={{ fontSize: '0.95rem' }}>
                      Select a policy from the sidebar to read
                    </Typography>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
        </div>
      </div>
    </ThemeBackground>
  );
};

export default PoliciesAndProcedures;
